<script setup lang="ts">
import BaseModal from '@/components/base/BaseModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { useTemplateRef, ref, inject } from 'vue'
import { AppWindowEvent, GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import { useHelpers } from '@/composables/useHelpers'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { handleCustomError, sendParentWindowMessage } = useHelpers()
const socket = useSocket()

const modalRef = useTemplateRef('modalRef')
const selectedDifficulty = ref<string>('')

const difficulties = ['easy', 'medium', 'hard']

const selectDifficulty = (difficulty: string) => {
  selectedDifficulty.value = difficulty
}

const startGame = async () => {
  try {
    showLoader()
    await socket.sendEventAsync(GameEvent.PlayBot, {
      difficulty: selectedDifficulty.value.toLowerCase()
    })
    sendParentWindowMessage(AppWindowEvent.StartGame, { isMultiplayer: false })
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const open = () => {
  modalRef.value?.open()
}

const close = () => {
  modalRef.value?.close()
}

defineExpose({
  open,
  close
})
</script>

<template>
  <BaseModal ref="modalRef" class="lg:!px-16">
    <div class="text-center">
      <div class="mb-7 flex flex-col gap-y-0.5 lg:gap-y-1 items-center">
        <h2 class="text-xl lg:text-2xl font-bold text-white">You vs Computer</h2>
        <p class="text-xs font-medium text-cw-grey-2">Select difficulty level</p>
      </div>
      <div class="grid grid-cols-1 gap-y-3 items-center">
        <div class="flex flex-col gap-y-1">
          <button
            v-for="difficulty in difficulties"
            :key="difficulty"
            class="border rounded-lg p-3 lg::p-4 text-sm lg:text-base capitalize font-bold transition-all duration-200 hover:bg-[#FEFEFE]/10 hover:scale-105"
            :class="{
              'border-[#FEFEFE] text-[#FEFEFE] bg-transparent': selectedDifficulty !== difficulty,
              'border-[#FEFEFE] text-cw-dark !bg-[#FEFEFE] !shadow-lg': selectedDifficulty === difficulty
            }"
            @click="selectDifficulty(difficulty)"
          >
            {{ difficulty }}
          </button>
        </div>
        <div class="mx-5 *:w-full">
          <BaseButton background-color="var(--color-cw-purple)" border-color="#1B0623" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" @click="startGame" :disabled="!selectedDifficulty">
            <p class="w-full !text-sm md:!text-base xl:!text-xl">Start game</p>
          </BaseButton>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
